import React, { useEffect, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import { Input } from 'formik-antd';
import { Col, Row, Tooltip } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import { isNil } from 'lodash';
import cx from 'classnames';
import * as Yup from 'yup';

import { ExtraItemEnum } from 'enums';
import {
  BaseEmployeeModel,
  ResourceDownloadResponse,
  WorkerDetailViewModel,
} from 'api/backofficeApiClient';
import { clearFormError } from 'utils/formUtils';
import FormItem from 'components/formik/FormItem';
import BaseAlert from 'components/wrappers/BaseAlert';
import Radio from 'components/formik/Radio';
import { getContactLabel } from 'utils/assignmentUtils';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getWorkerInfoFile } from 'redux/reducers/workerSlice';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { WorkerTypeEnum } from 'api/adminApiClient';
import { getWorkerTypeLabel } from 'utils/workerUtils';
import { generateUploadFileList } from 'utils/fileUtils';
import { BaseUploadFile } from 'components/wrappers/BaseFileUpload';
import { WorkerInfoFormValues as W2WorkerInfoFormValues } from '../W2Assignments/WorkerInfo';
import { WorkerInfoFormValues as ICWorkerInfoFormValues } from '../ICAssignments/WorkerInfo';
import { WorkerInfoFormValues as SCWorkerInfoFormValues } from '../SCAssignments/WorkerInfo';
import WorkerUpload from 'components/modals/WorkerModal/WorkerUpload';
import InputNumber from 'components/formik/InputNumber';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { countryUSAid } from 'constants/index';
import InternationalWorkerStateForm from './InternationalWorkerStateForm';
import WorkerStateForm from './WorkerStateForm';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';

import styles from '../AssignmentModal/AssignmentModal.module.less';

export type WorkerInfoFormValues =
  | W2WorkerInfoFormValues
  | ICWorkerInfoFormValues
  | SCWorkerInfoFormValues;

type Props<T extends WorkerInfoFormValues> = {
  formik: FormikProps<T>;
  predefinedInitialValues?: T;
  workers: WorkerDetailViewModel[];
  workerFileInfo?: ResourceDownloadResponse;
  isWorkersLoaded?: boolean;
  isRenewOrEditRenewMode: boolean;
  isRehireOrEditRehireMode: boolean;
  hasActiveAssignmentRestriction: boolean;
};

export interface WorkerFormValues {
  workerId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  workerType?: WorkerTypeEnum;
  country?: number;
  internationalCityName?: string;
  internationalStateName?: string;
  state?: number;
  city: string;
  address: string;
  zip: string;
}

export const workerInitialValidationSchema = {
  workerId: Yup.string(),
  firstName: Yup.string()
    .trim()
    .max(120, 'First name should be 120 characters maximum'),
  lastName: Yup.string()
    .trim()
    .max(120, 'Last name should be 120 characters maximum')
    .workerDuplicateCheck(),
  email: Yup.string()
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum')
    .workerDuplicateCheck(),
  phone: Yup.string()
    .trim()
    .max(120, 'Phone number should be 120 characters maximum'),
  workerType: Yup.number().workerDuplicateCheck(),
  country: Yup.number().nullable(),
  state: Yup.string().trim().nullable(),
  internationalStateName: Yup.string()
    .nullable()
    .trim()
    .max(120, `State should be 120 characters maximum`),
  city: Yup.string()
    .trim()
    .nullable()
    .max(120, 'City should be 120 characters maximum'),
  internationalCityName: Yup.string()
    .nullable()
    .trim()
    .max(120, 'City should be 120 characters maximum'),
  address: Yup.string()
    .trim()
    .max(120, 'Address should be 120 characters maximum'),
  zip: Yup.string()
    .trim()
    .max(120, 'ZIP code should be 120 characters maximum'),
};

const workerTypes = [
  {
    value: WorkerTypeEnum.IC,
    label: getWorkerTypeLabel(WorkerTypeEnum.IC),
  },
  {
    value: WorkerTypeEnum.InternationalIC,
    label: getWorkerTypeLabel(WorkerTypeEnum.InternationalIC),
  },
];

export const workerInitialValues: WorkerFormValues = {
  workerId: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  country: undefined,
  workerType: undefined,
  internationalStateName: undefined,
  internationalCityName: undefined,
  state: undefined,
  city: '',
  address: '',
  zip: '',
};

export const isAddWorkerSelected = (option: string) =>
  option === ExtraItemEnum.AddWorker;

export default function WorkerSection<T extends WorkerInfoFormValues>(
  props: Props<T>,
) {
  const {
    formik,
    predefinedInitialValues,
    workerFileInfo,
    workers,
    isWorkersLoaded,
    isRenewOrEditRenewMode,
    isRehireOrEditRehireMode,
    hasActiveAssignmentRestriction,
  } = props;
  const [workerFileKeyInfo, setWorkerFileKeyInfo] = useState<BaseUploadFile[]>(
    workerFileInfo ? generateUploadFileList(workerFileInfo) : [],
  );
  const [workerDetails, setWorkerDetails] = useState<WorkerDetailViewModel>();
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const [filteredWorkers, setFilteredWorkers] = useState<
    WorkerDetailViewModel[]
  >([]);
  const workerFieldRef: any = useRef();
  const isAddWorkerOptionSelected = isAddWorkerSelected(
    formik.values.worker?.workerId,
  );
  const isICICInternationalWorkerType =
    formik.values.worker.workerType === WorkerTypeEnum.IC ||
    formik.values.worker.workerType === WorkerTypeEnum.InternationalIC;

  useEffect(() => {
    const predefinedWorkerId = predefinedInitialValues?.worker.workerId;

    if (!predefinedWorkerId) return;

    if (isAddWorkerSelected(predefinedWorkerId)) return;

    const initialWorker = workers.find(
      (worker) => worker.id === predefinedWorkerId,
    );

    if (!initialWorker) return;

    setWorkerDetails(initialWorker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predefinedInitialValues?.worker.workerId, workers]);

  useEffect(() => {
    setFilteredWorkers(
      workers.filter(
        (worker) => worker.workerType === formik.values.worker.workerType,
      ),
    );
  }, [formik.values.worker.workerType, workers]);

  const loadWorkerFile = async (workerId?: string) => {
    if (!workerId || !isICICInternationalWorkerType) return;

    const result = await dispatch(
      getWorkerInfoFile({
        workerId,
        isAdminRole: isAdmin as boolean,
      }),
    );

    if (getWorkerInfoFile.fulfilled.match(result)) {
      setWorkerFileKeyInfo(generateUploadFileList(result.payload));
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleResetWorkerInfo = () => {
    setWorkerFileKeyInfo([]);
    setWorkerDetails(undefined);
  };

  const handleWorkerStateChange = () => {
    formik.setFieldValue('worker.city', '', false);
  };

  const handleWorkerChange = async (
    workerId: string,
    formik: FormikProps<T>,
  ) => {
    const selectedWorker = workers.find(({ id }) => id === workerId);
    const isAddWorker = isAddWorkerSelected(workerId);

    if (isAddWorker) {
      handleResetWorkerInfo();
    } else {
      if (!selectedWorker?.workerInfo?.workerFileKey) {
        setWorkerFileKeyInfo([]);
      } else {
        await loadWorkerFile(selectedWorker?.id);
      }

      setWorkerDetails(selectedWorker);
      handleWorkerStateChange();
    }

    formik.setFieldTouched('worker', false, false);
    formik.setFieldValue(
      'worker',
      {
        workerId,
        firstName: !isAddWorker ? selectedWorker?.workerInfo?.firstName : '',
        lastName: !isAddWorker ? selectedWorker?.workerInfo?.lastName : '',
        email: !isAddWorker ? selectedWorker?.workerInfo?.email : '',
        phone: !isAddWorker ? selectedWorker?.workerInfo?.phoneNumber : '',
        workerType: formik.values.worker.workerType,
        country: !isAddWorker
          ? //Added a new country field so need that everyone has USA by default
            selectedWorker?.location?.countryId || countryUSAid
          : countryUSAid,
        state: !isAddWorker ? selectedWorker?.location?.stateId : undefined,
        internationalCityName: !isAddWorker
          ? selectedWorker?.location?.internationalCityName
          : undefined,
        internationalStateName: !isAddWorker
          ? selectedWorker?.location?.internationalStateName
          : undefined,
        city: !isAddWorker ? selectedWorker?.location?.city : '',
        address: !isAddWorker ? selectedWorker?.location?.address : '',
        zip:
          !isNil(selectedWorker?.location?.zipCode) && !isAddWorker
            ? selectedWorker?.location?.zipCode
            : '',
      },
      false,
    );

    if (!isICICInternationalWorkerType) return;

    formik.setFieldValue(
      'taxId',
      !isAddWorker ? selectedWorker?.workerInfo?.taxId : null,
      false,
    );

    formik.setFieldValue(
      'workerFileKey',
      !isAddWorker && selectedWorker?.workerInfo?.workerFileKey
        ? [selectedWorker?.workerInfo?.workerFileKey]
        : [],
      false,
    );
  };

  const handleICWorkerTypeChange = (e: RadioChangeEvent) => {
    clearFormError(formik, 'worker._formError');

    if (isAddWorkerOptionSelected) return;

    handleResetWorkerInfo();

    formik.setFieldValue(
      'worker',
      {
        workerId: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        workerType: e.target.value,
        country: null,
        state: undefined,
        city: '',
        address: '',
        zip: '',
      },
      false,
    );
    formik.setFieldValue('taxId', null, false);
    formik.setFieldValue('workerFileKey', [], false);
  };

  const isUploaderDisabled = () => {
    return (
      !formik.values.worker.workerId ||
      isRenewOrEditRenewMode ||
      isRehireOrEditRehireMode ||
      !!workerDetails?.workerInfo?.workerFileKey ||
      hasActiveAssignmentRestriction
    );
  };

  return (
    <>
      <div className="modal-title">Worker information</div>
      {(isRenewOrEditRenewMode ||
        isRehireOrEditRehireMode ||
        hasActiveAssignmentRestriction) && (
        <div className={styles.hint}>
          <i className="icon icon-info-filled" />
          Worker info cannot be modified due to MBP Payments to Worker. Use Back
          Office's Worker module to update contact details or reach out to your
          Success Partner
        </div>
      )}
      <Row gutter={40}>
        <Col xs={24} md={12} ref={workerFieldRef}>
          <FormItem
            name="worker.workerId"
            label={
              <>
                <span>Worker *</span>
                <Tooltip
                  overlayClassName={cx('light-tooltip', styles.modalTooltip)}
                  title="This option lets you Create a new worker or Select an existing one."
                  trigger="click"
                  placement="topLeft"
                  getPopupContainer={() => workerFieldRef.current}
                  className={styles.modalTooltipOpener}
                >
                  <span className="icon icon-attention-filled" />
                </Tooltip>
              </>
            }
          >
            <FormikSelectWithLoading
              isLoading={!isWorkersLoaded}
              showSearch
              name="worker.workerId"
              placeholder="Select worker"
              onChange={(value: string) => handleWorkerChange(value, formik)}
              optionFilterProp="label"
              disabled={
                isRenewOrEditRenewMode ||
                isRehireOrEditRehireMode ||
                hasActiveAssignmentRestriction
              }
            >
              {isWorkersLoaded && (
                <>
                  <FormikSelectWithLoading.Option
                    key="select-other-worker"
                    value={ExtraItemEnum.AddWorker}
                  >
                    {ExtraItemEnum.AddWorker}
                  </FormikSelectWithLoading.Option>
                  {filteredWorkers.map((worker) => (
                    <FormikSelectWithLoading.Option
                      key={`select-worker-${worker.id}`}
                      value={worker.id || ''}
                      label={getContactLabel(
                        worker.workerInfo as BaseEmployeeModel,
                      )}
                    >
                      {getContactLabel(worker.workerInfo as BaseEmployeeModel)}
                    </FormikSelectWithLoading.Option>
                  ))}
                </>
              )}
            </FormikSelectWithLoading>
          </FormItem>
        </Col>
      </Row>

      <Row gutter={40}>
        <Col xs={24} md={12}>
          <FormItem
            name="worker.firstName"
            label="First name *"
            data-id="newAssignmentWorkerFirstNameField"
          >
            <Input
              name="worker.firstName"
              placeholder="Enter first name"
              disabled={!isAddWorkerOptionSelected}
              autoComplete="off"
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="worker.lastName"
            label="Last name *"
            data-id="newAssignmentWorkerLastNameField"
          >
            <Input
              name="worker.lastName"
              placeholder="Enter last name"
              disabled={!isAddWorkerOptionSelected}
              onChange={() => clearFormError(formik, 'worker._formError')}
              autoComplete="off"
            />
          </FormItem>
        </Col>
      </Row>

      <Row gutter={40}>
        <Col xs={24} md={12}>
          <FormItem
            name="worker.email"
            label="Email *"
            data-id="newAssignmentWorkerEmailField"
          >
            <Input
              name="worker.email"
              placeholder="Enter email"
              disabled={!isAddWorkerOptionSelected}
              onChange={() => clearFormError(formik, 'worker._formError')}
              autoComplete="off"
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            data-id="newAssignmentWorkerPhoneField"
            label="Phone number *"
            name="worker.phone"
          >
            <Input
              name="worker.phone"
              placeholder="Enter phone number"
              disabled={!isAddWorkerOptionSelected}
              autoComplete="off"
            />
          </FormItem>
        </Col>
      </Row>

      {isICICInternationalWorkerType ? (
        <>
          {(isRenewOrEditRenewMode ||
            isRehireOrEditRehireMode ||
            hasActiveAssignmentRestriction) && (
            <div className={styles.hint}>
              <i className="icon icon-info-filled" />
              Worker Type cannot be modified for compliance
            </div>
          )}
          <div className="modal-title">Worker type</div>
          <Row gutter={40}>
            <Col xs={24} md={12}>
              <FormItem name="worker.workerType">
                <Radio.Group
                  name="worker.workerType"
                  onChange={handleICWorkerTypeChange}
                  options={workerTypes}
                  disabled={
                    isRenewOrEditRenewMode ||
                    isRehireOrEditRehireMode ||
                    hasActiveAssignmentRestriction
                  }
                />
              </FormItem>
              <FormItem data-id="workerTaxIdField" label="Tax ID" name="taxId">
                <InputNumber
                  name="taxId"
                  disabled={
                    !(
                      formik.values.worker.workerId &&
                      isNil(workerDetails?.workerInfo?.taxId)
                    ) ||
                    isRenewOrEditRenewMode ||
                    isRehireOrEditRehireMode ||
                    hasActiveAssignmentRestriction
                  }
                  placeholder="Enter tax ID"
                />
              </FormItem>
            </Col>
          </Row>
          <Row gutter={40}>
            <Col xs={24} md={12}>
              <h5 className="modal-title">
                Please use this section to upload worker's W9 Form
              </h5>
              <div className="upload-wrapper">
                <FormItem name="workerFileKey" showValidateError={false}>
                  <WorkerUpload
                    workerType={formik.values.worker.workerType}
                    isAdmin={isAdmin as boolean}
                    fieldName="workerFileKey"
                    workerFiles={workerFileKeyInfo}
                    disabled={isUploaderDisabled()}
                    onChange={setWorkerFileKeyInfo}
                  />
                </FormItem>
              </div>
            </Col>
          </Row>
        </>
      ) : (
        <Row gutter={40}>
          <Col xs={24} md={12}>
            <FormItem name="worker.workerType" label="Worker type *">
              <Input
                name="worker.workerType"
                placeholder="Enter worker type"
                disabled
                value={getWorkerTypeLabel(formik.values.worker.workerType)}
                onChange={() => clearFormError(formik, 'worker._formError')}
                autoComplete="off"
              />
            </FormItem>
          </Col>
        </Row>
      )}

      <div className="modal-title">Home address</div>

      {checkIsCountriesWithStatesDropdown(formik.values.worker.country) ? (
        <WorkerStateForm<T>
          formik={formik}
          isAddWorkerOptionSelected={isAddWorkerOptionSelected}
        />
      ) : (
        <InternationalWorkerStateForm<T>
          formik={formik}
          isAddWorkerOptionSelected={isAddWorkerOptionSelected}
        />
      )}
    </>
  );
}
